import { FC, Fragment, useEffect, useState, useRef } from "react";
import useOutsideClick from "hooks/click-outside";
import { BadgeDTO } from "utils/helpers/models/host-dtos/badge.dto";
import { PictureDTO } from "utils/helpers/models/shared/picture.dto";
import useHost from "containers/host/useHooks";
import DataNotFound from "components/data-not-found";
import { useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import FormErrorMessage from "components/forms/form-error-message";
import {
  BAGDE_TYPES,
  SUBSCRIPTION_TYPES,
} from "utils/helpers/enums/shared.enum";
import { warningToaster } from "utils/helpers/common/alert-service";
import useAuth from "containers/auth/useHooks";
import { BlueBackIcon } from "assets/images/svgs";
import bgCards from "assets/images/others/host-side-images/bgCards.png";
import noBadgeFound from "assets/images/gifs/badge-not-found.gif";
import CustomMessageDisplay from "components/data-not-found/CustomMessageDisplay";

interface Props {
  isOpenBadgeModal: boolean;
  onCloseBadgeModal: (badge?: BadgeDTO) => void;
}

const BadgeModal: FC<Props> = ({ isOpenBadgeModal, onCloseBadgeModal }) => {
  const [selectedBadge, setSelectedBadge] = useState<BadgeDTO>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [formData, setFormData] = useState<BadgeDTO>(new BadgeDTO());
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { isLoading } = useSelector((state: any) => state.sharedReducer);
  const modalRef = useOutsideClick(onCloseBadgeModal);
  const [data, setData] = useState<BadgeDTO[]>([]);
  const { getBadges, getAdminBadges, createBadge } = useHost();
  const { uploadFile } = useAuth();
  const {
    register,
    setValue,
    handleSubmit,
    formState: { errors },
  } = useForm<BadgeDTO>();

  const registeredData = useSelector(
    (state: any) => state.sharedReducer.registeredData
  );

  // Determine initial active tab based on subscription type
  const getInitialActiveTab = () => {
    const subscriptionType =
      registeredData?.user?.subscription?.product?.subscriptionType;
    // Show "myBadges" only for TIER_1 and TIER_2, default to "adminBadges" for BASIC
    return subscriptionType === SUBSCRIPTION_TYPES.BASIC
      ? "adminBadges"
      : "myBadges";
  };

  const [activeTab, setActiveTab] = useState(getInitialActiveTab);

  const handleSelectAttendee = (badge: BadgeDTO) => {
    setSelectedBadge(badge);
  };

  const handleBadgeSubmit = () => {
    if (selectedBadge) {
      onCloseBadgeModal(selectedBadge);
    }
  };

  // Update active tab when registeredData changes
  useEffect(() => {
    const newActiveTab = getInitialActiveTab();
    if (newActiveTab !== activeTab) {
      setActiveTab(newActiveTab);
    }
  }, [registeredData]);

  useEffect(() => {
    // Initialize with the correct data based on active tab
    if (activeTab === "adminBadges") {
      getAdminBadges(setData);
    } else {
      getBadges(setData);
    }
  }, [activeTab]);

  // Cleanup object URL on unmount or when image changes
  useEffect(() => {
    return () => {
      if (imagePreview) {
        URL.revokeObjectURL(imagePreview);
      }
    };
  }, [imagePreview]);

  const handleChangeTab = (tab: string) => {
    // Clear data first to prevent showing old images
    setData([]);
    setActiveTab(tab);

    // Then load new data
    if (tab === "adminBadges") {
      getAdminBadges(setData);
    } else {
      getBadges(setData);
    }
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { files } = event.target;
    const file = files[0];
    if (file) {
      // Create preview URL
      const previewUrl = URL.createObjectURL(file);
      setImagePreview(previewUrl);

      formData.imageUrl = file.name;
      const picture = await uploadFile(file);
      if (picture) {
        formData.imageId = picture.id;
        formData.image = picture;
      }
      setFormData({ ...formData });
    }
  };

  const handleRemoveImage = () => {
    if (imagePreview) {
      URL.revokeObjectURL(imagePreview);
    }
    setImagePreview(null);
    setFormData((prev) => ({
      ...prev,
      imageUrl: "",
      imageId: 0,
      image: new PictureDTO(),
    }));
    // Reset the file input so the same file can be uploaded again
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const onSubmitBadge = async () => {
    if (!formData.imageId) {
      warningToaster("Badge image is required");
    } else {
      const badgeData = {
        ...formData,
        badgeType: BAGDE_TYPES.EVENT,
      };
      await createBadge(badgeData);
      // Refresh the badges list based on current active tab
      if (activeTab === "myBadges") {
        getBadges(setData);
      } else {
        getAdminBadges(setData);
      }
      setShowCreateForm(false);
      setFormData(new BadgeDTO());
      setImagePreview(null);
    }
  };

  return (
    <div
      className={`fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center transition-opacity duration-300 ${
        isOpenBadgeModal ? "opacity-100" : "opacity-0 pointer-events-none"
      }`}
      style={{ zIndex: 'calc(var(--z-modal) + 1)' }}
    >
      <div
        ref={modalRef}
        className={`relative bg-white px-6 py-8 rounded-lg shadow-lg w-[90%] sm:h-3/4 md:w-1/2 transform transition-transform duration-300 overflow-y-auto ${
          isOpenBadgeModal ? "scale-100" : "scale-95"
        }`}
      >
        {/* Close Button */}
        <button
          className="absolute top-3 right-3 text-gray-500 hover:text-gray-700"
          onClick={() => onCloseBadgeModal()}
        >
          ×
        </button>

        {/* Modal Title */}
        <h2 className="text-xl font-semibold text-gray-800 text-center">
          {showCreateForm ? "Create New Badge" : "Badges"}
        </h2>

        {showCreateForm ? (
          /* Create Badge Form */
          <div className="mt-4">
            <button
              className="flex items-center text-gray-500 mb-4"
              type="button"
              onClick={() => setShowCreateForm(false)}
            >
              <span className="mr-2">
                <BlueBackIcon />
              </span>{" "}
              Back to Badges
            </button>

            <form className="space-y-4">
              <div className="">
                <div className="flex items-center gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Upload Badge Image *
                    </label>
                    <div className="relative flex items-center">
                      <label className="bg-gray-200 px-4 py-2 rounded-lg cursor-pointer hover:bg-gray-300 transition-colors">
                        + Upload Image
                        <input
                          ref={fileInputRef}
                          type="file"
                          className="hidden"
                          onChange={handleFileChange}
                          accept="image/*"
                        />
                      </label>
                    </div>
                  </div>

                  {imagePreview && (
                    <div className="relative w-12 h-12 mt-7">
                      <img
                        src={imagePreview}
                        alt="Preview"
                        className="w-full h-full object-cover rounded"
                      />
                      <button
                        type="button"
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 text-xs"
                        onClick={handleRemoveImage}
                      >
                        ×
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Save Button */}
              <div className="flex justify-center mt-6">
                <button
                  className="bg-primaryBlueBg text-white px-6 py-2 rounded-lg "
                  type="button"
                  onClick={onSubmitBadge}
                >
                  Save Badge
                </button>
              </div>
            </form>
          </div>
        ) : (
          /* Badge List View */
          <div>
            {/* Tabs */}
            <div className="flex gap-4 mt-4">
              {/* Show My Badges tab only for TIER_1 and TIER_2 users */}
              {registeredData?.user?.subscription?.product?.subscriptionType !==
                SUBSCRIPTION_TYPES.BASIC && (
                <button
                  className={`py-2 px-4 transition duration-300 ${
                    activeTab === "myBadges"
                      ? "text-lightBlueColor border-b-2 border-lightBlueColor"
                      : "text-grayText hover:text-lightBlueColor"
                  }`}
                  onClick={() => handleChangeTab("myBadges")}
                >
                  My Badges
                </button>
              )}
              <button
                className={`py-2 px-4 transition duration-300 ${
                  activeTab === "adminBadges"
                    ? "text-lightBlueColor border-b-2 border-lightBlueColor"
                    : "text-grayText hover:text-lightBlueColor"
                }`}
                onClick={() => handleChangeTab("adminBadges")}
              >
                Admin Badges
              </button>
            </div>

            {/* Tab Content */}
            <div className="mt-4 ">
              <div className="flex flex-wrap gap-4 py-5">
                {data.map((badge: any, index) => (
                  <div
                    key={index}
                    style={{
                      backgroundImage: `url(${bgCards})`,
                    }}
                    className={`relative flex flex-col items-center justify-center p-4 rounded-lg w-[180px] border min-h-[180px] cursor-pointer transition-all duration-300 ${
                      selectedBadge?.id === badge.id
                        ? "border-yellow-500"
                        : "border-[#D9D7D7]"
                    }`}
                    onClick={() => handleSelectAttendee(badge)}
                  >
                    <div className="absolute top-2 right-2 w-6 h-6 flex items-center justify-center">
                      <input
                        type="radio"
                        name="attendee"
                        className="custom-checkbox"
                        checked={selectedBadge?.id === badge.id}
                        onChange={() => handleSelectAttendee(badge)}
                      />
                    </div>

                    <img
                      key={`${activeTab}-${badge.id}-${index}`}
                      className="w-[98px] min-h-[108px] object-cover transition-transform duration-200 hover:scale-105"
                      src={badge.image.name}
                      alt={badge.name}
                    />

                    <span className="text-sm font-semibold mt-2 text-black">
                      {badge?.name}
                    </span>
                  </div>
                ))}
              </div>
              {data.length > 0 && (
                <div className="flex justify-center">
                  <button
                    className={`flex justify-center sm:w-1/3 px-4 mt-4 py-2 rounded-full font-semibold ${
                      selectedBadge
                        ? "bg-primaryBlueBg text-white cursor-pointer"
                        : "bg-gray-300 text-gray-500 cursor-not-allowed"
                    }`}
                    onClick={handleBadgeSubmit}
                    disabled={!selectedBadge}
                  >
                    Upload Badge
                  </button>
                </div>
              )}
              <div className="">
                <CustomMessageDisplay
                  show={!isLoading && !data.length}
                  title={
                    activeTab === "myBadges"
                      ? "No Badges Available"
                      : "No Admin Badges Available"
                  }
                  slogan={
                    activeTab === "myBadges"
                      ? "You haven't created any badges yet. Click 'Add New Badge' to create your first badge!"
                      : "No admin badges are available at the moment. Admin badges will appear here once they are added."
                  }
                  image={noBadgeFound}
                  imageAlt="No badges found"
                  className="py-12"
                  imageClassName="w-[150px] h-[120px]"
                  titleClassName="text-2xl font-bold text-blackie"
                  sloganClassName="text-gray-500 text-base"
                />
              </div>
              {activeTab === "myBadges" &&
                registeredData?.user?.subscription?.product
                  ?.subscriptionType !== SUBSCRIPTION_TYPES.BASIC && (
                  <div className="flex justify-center mt-4">
                    <button
                      className="flex justify-center sm:w-1/3 px-4 py-2 rounded-full font-semibold bg-fade text-primaryBlueText transition-colors duration-200"
                      onClick={() => setShowCreateForm(true)}
                    >
                      + Add New Badge
                    </button>
                  </div>
                )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BadgeModal;
